import { useRef, useState, useEffect, useContext, useLayoutEffect } from "react";
import { DialogType, Stack } from "@fluentui/react";
import { ErrorCircleRegular } from "@fluentui/react-icons";

import uuid from 'react-uuid';
import { isEmpty } from "lodash-es";

import styles from "./Chat.module.css";
import Contoso from "../../assets/Contoso.svg";

import {
    ChatMessage,
    ConversationRequest,
    conversationApi,
    Citation,
    ToolMessageContent,
    ChatResponse,
    getUserInfo,
    Conversation,
    historyUpdate,
    historyClear,
    ChatHistoryLoadingState,
    CosmosDBStatus,
    ErrorMessage,
    FlowType,
    MessageType,
    sendData,
    Assessments,
    Assessments_IDs
} from "../../api";
import { Answer } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { AppStateContext } from "../../state/AppProvider";
import { useBoolean } from "@fluentui/react-hooks";
import UserResponsesRender from "./UserResponsesRender";
import { discoverHowWeCanHelpMessages, getNewResponseAndFilter, initialMessages, resolveITMessages, delay, askForConsultationMessage, askForEmailMessage, askMoreDetailMessage, consultationFormMessages, submitFormResponseMessage, askAreasToAssess, askForConsultationAssessmentMessage, getFormatMessage, idToTextObj, } from "../util";
import Suggestions from "./Suggestions";
import InactiveModal from "./InactiveModal";
import KillSessionModal from "./KillSessionModal";

const enum messageStatus {
    NotRunning = "Not Running",
    Processing = "Processing",
    Done = "Done"
}

const NSChat = () => {
    const appStateContext = useContext(AppStateContext)
    const ui = appStateContext?.state.frontendSettings?.ui;
    const AUTH_ENABLED = appStateContext?.state.frontendSettings?.auth_enabled;
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false);
    const [activeCitation, setActiveCitation] = useState<Citation>();
    const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false);
    const abortFuncs = useRef([] as AbortController[]);
    const [showAuthMessage, setShowAuthMessage] = useState<boolean>(true);
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [processMessages, setProcessMessages] = useState<messageStatus>(messageStatus.NotRunning);
    const [clearingChat, setClearingChat] = useState<boolean>(false);
    const [hideErrorDialog, { toggle: toggleErrorDialog }] = useBoolean(true);
    const [errorMsg, setErrorMsg] = useState<ErrorMessage | null>()
    const [emailCapture, setEmailCapture] = useState<boolean>(false)
    const [flowNumber, setFlowNumber] = useState<number>(0)
    const [assessmentQuestions, setAssessmentQuestions] = useState<string[] | null>(null)
    const [assessmentQuestionsIndex, setAssessmentQuestionsIndex] = useState<number>(0)
    const [assessmentCurrentQuestion, setAssessmentCurrentQuestion] = useState<string | null>(null)
    const [showSuggestion, setShowSuggestion] = useState<boolean | string>(false)
    const [assessmentFlow, setAssessmentFlow] = useState<boolean | string | null>(false)
    const [question, setQuestion] = useState<string>("");
    const [showKillSessionModal, setshowKillSessionModal] = useState<boolean>(false);
    const [oocCount, setoocCount] = useState<number>(0);
    const [formSubmitted, setFormSubmitted] = useState<boolean>(false);

    const errorDialogContentProps = {
        type: DialogType.close,
        title: errorMsg?.title,
        closeButtonAriaLabel: 'Close',
        subText: errorMsg?.subtitle,
    };

    const modalProps = {
        titleAriaId: 'labelId',
        subtitleAriaId: 'subTextId',
        isBlocking: true,
        styles: { main: { maxWidth: 450 } },
    }

    const [ASSISTANT, TOOL, ERROR] = ["assistant", "tool", "error"]
    const NO_CONTENT_ERROR = "No content in messages object."

    useEffect(() => {
        const conversation: Conversation = {
            id: uuid(),
            title: "Welcome to New Scaler",
            messages: initialMessages,
            date: new Date().toISOString()
        };
        let currentChatInSession = sessionStorage.getItem('currentChat') || null;
        let payload;
        if (currentChatInSession) {
            payload = JSON.parse(currentChatInSession);
        } else {
            payload = conversation;
        }
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload });

        window.addEventListener('beforeunload', () => {
            sessionStorage.clear();
        });

        return () => {
            window.removeEventListener('beforeunload', () => {
                sessionStorage.clear();
            });
        }
    }, [])

    useEffect(() => {
        if (sessionStorage.getItem('CurrentAssessmentFlow'))
            setAssessmentFlow(sessionStorage.getItem('CurrentAssessmentFlow'))
    }, [sessionStorage.getItem('CurrentAssessmentFlow')])

    useEffect(() => {
        if (appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.Working
            && appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured
            && appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail
            && hideErrorDialog) {
            let subtitle = `${appStateContext.state.isCosmosDBAvailable.status}. Please contact the site administrator.`
            setErrorMsg({
                title: "Chat history is not enabled",
                subtitle: subtitle
            })
            toggleErrorDialog();
        }
    }, [appStateContext?.state.isCosmosDBAvailable]);

    const handleErrorDialogClose = () => {
        toggleErrorDialog()
        setTimeout(() => {
            setErrorMsg(null)
        }, 500);
    }

    useEffect(() => {
        setIsLoading(appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading)
    }, [appStateContext?.state.chatHistoryLoadingState])

    const clearAssessment = () => {

        setAssessmentQuestions(null)
        setAssessmentQuestionsIndex(0)
        setAssessmentCurrentQuestion(null)
        setShowSuggestion(false)
        setAssessmentFlow(null)
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_AI_CHAT', payload: null });
        appStateContext?.dispatch({ type: 'UPDATE_ASSESSMENT_CHAT', payload: null });
        sessionStorage.removeItem('CurrentAssessmentFlow');
        sessionStorage.removeItem('assessmentChat');
        setFlowNumber(0)
    }

    const getUserInfoList = async () => {
        if (!AUTH_ENABLED) {
            setShowAuthMessage(false);
            return;
        }
        const userInfoList = await getUserInfo();
        if (userInfoList.length === 0 && window.location.hostname !== "127.0.0.1") {
            setShowAuthMessage(true);
        }
        else {
            setShowAuthMessage(false);
        }
    }

    let assistantMessage = {} as ChatMessage
    let nextRespopnseMessage = [] as ChatMessage[]
    let toolMessage = {} as ChatMessage
    let assistantContent = ""
    let disableAIChatUpdate = false

    const responseInterceptor = async (resultMessage: ChatMessage, conversationId?: string) => {
        const respMessage = resultMessage.content
        const actions: { [key: string]: (() => any) | any } = {
            'Out of context': () => {
                disableAIChatUpdate = true
                if (oocCount >= 2) {
                    setshowKillSessionModal(true)
                    assistantMessage = {} as ChatMessage
                    return true
                }

                const aiConversation = {
                    id: uuid(),
                    title: 'New chat',
                    messages: [...askMoreDetailMessage],
                    date: new Date().toISOString(),
                };
                nextRespopnseMessage.push(...askMoreDetailMessage)
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_AI_CHAT', payload: aiConversation });
                setoocCount(oocCount + 1)
                return true
            },
            'Consultation': () => {
                setFlowNumber(1)
                nextRespopnseMessage.push(...askForConsultationMessage)
                return true
            },
        };

        const matchingKey = Object.keys(actions).find(key => respMessage.toLowerCase().includes(key.toLowerCase()));

        if (matchingKey) {
            return actions[matchingKey]();
        }

        if (sessionStorage.getItem("CurrentAssessmentFlow") || assessmentFlow) {
            if (assessmentQuestions?.length == assessmentQuestionsIndex + 1) {
                nextRespopnseMessage.push(...askForConsultationAssessmentMessage)
                clearAssessment()
                return false
            } else {
                let questions = respMessage.split(";").map((q) => q.replace(/[{}]/g, "")).filter((x) => x)
                setAssessmentQuestions(questions)
                resultMessage.content = questions[assessmentQuestionsIndex]
                const assessmentConversation = appStateContext?.state?.assessmentChat || []
                assessmentConversation.push(`ASSISTANT: ${questions[0]}`);
                setAssessmentCurrentQuestion(questions[0])
                appStateContext?.dispatch({ type: 'UPDATE_ASSESSMENT_CHAT', payload: assessmentConversation });
                setAssessmentQuestionsIndex(assessmentQuestionsIndex + 1)
                setShowSuggestion(true)
                return true

            }

        }


        return false;
    }

    const processResultMessage = (resultMessage: ChatMessage, userMessage: ChatMessage, conversationId?: string) => {
        if (resultMessage.role === ASSISTANT) {
            assistantContent += resultMessage.content
            assistantMessage = resultMessage
            assistantMessage.content = assistantContent
            assistantMessage.messageType = MessageType.Text

            if (resultMessage.context) {
                toolMessage = {
                    id: uuid(),
                    role: TOOL,
                    content: resultMessage.context,
                    date: new Date().toISOString(),
                    flowtype: resultMessage.flowtype,
                    messageType: MessageType.Text
                }
            }
        }

        if (resultMessage.role === TOOL) toolMessage = resultMessage
    }

    const nextActionConversation = (option: string, nextActionMessages: ChatMessage[]) => {
        let conversation = appStateContext?.state?.currentChat;

        if (conversation) {
            const updatedConversation = getNewResponseAndFilter(conversation, option, nextActionMessages);
            appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: updatedConversation || null });
            setMessages(updatedConversation?.messages || []);
        }
    }

    const delayResponse = async (ms: number) => {
        setIsLoading(true);
        setShowLoadingMessage(true);
        await delay(ms);
        setIsLoading(false);
        setShowLoadingMessage(false);
    }

    const submitFormResponse = () => {
        let conversation = appStateContext?.state?.currentChat;
        if (conversation) {
            conversation.messages = conversation.messages.filter((answer) => answer.messageType !== MessageType.ConsultationForm);
            const updatedConversation = getNewResponseAndFilter(conversation, 'Response submitted', submitFormResponseMessage);
            delayResponse(300)
            appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: updatedConversation || null });
            clearAssessment()
            setFormSubmitted(true)
            setMessages(updatedConversation?.messages || []);
        }
    }

    const askMoreDeatils = (option: string, fNum?: number) => {
        let _fNum = fNum || flowNumber;
        nextActionConversation(option, (_fNum == 1) ? askMoreDetailMessage : askAreasToAssess);

        const aiConversation = appStateContext?.state?.currentAIChat || {
            id: uuid(),
            title: option,
            messages: [],
            date: new Date().toISOString(),
        };
        setEmailCapture(true)
        aiConversation.messages.push(...((_fNum == 1) ? askMoreDetailMessage : []));
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_AI_CHAT', payload: aiConversation });
    }

    const requestInterceptor = async (option: string) => {
        const conversation = appStateContext?.state?.currentChat
        const lastMessage = conversation?.messages[conversation?.messages.length - 1]

        const actions: { [key: string]: (() => any) | any } = {
            'Discover How we can help': () => {
                nextActionConversation(option, discoverHowWeCanHelpMessages);
                return true
            },
            'Assess Security': () => {
                setFlowNumber(2);
                if (emailCapture) {
                    askMoreDeatils(option, 2);
                    return true;
                }
                nextActionConversation(option, askForEmailMessage);
                return true
            },
            'Assess another area': () => {
                setFlowNumber(2);
                if (emailCapture) {
                    askMoreDeatils(option, 2);
                    return true;
                }
                nextActionConversation(option, askForEmailMessage);
                return true
            },
            'Resolve IT Challenges': () => {
                setFlowNumber(1);
                if (emailCapture) {
                    askMoreDeatils(option, 1);
                    return true;
                }
                nextActionConversation(option, askForEmailMessage);
                return true
            },
            'Resolve another IT Challenge': () => {
                nextActionConversation(option, askMoreDetailMessage);
                return true;
            },
            'open form': () => {
                nextActionConversation('Yes', consultationFormMessages);
                return true
            },
            [Assessments_IDs.Identity_Security]: () => {
                option = idToTextObj[Assessments_IDs.Identity_Security];
                appStateContext?.dispatch({ type: 'UPDATE_ASSESSMENT_CHAT', payload: null });
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_AI_CHAT', payload: null });
                sessionStorage.setItem('CurrentAssessmentFlow', FlowType.IdentityAssessment)
                return false
            },
            [Assessments_IDs.Data_Security]: () => {
                option = idToTextObj[Assessments_IDs.Data_Security];
                appStateContext?.dispatch({ type: 'UPDATE_ASSESSMENT_CHAT', payload: null });
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_AI_CHAT', payload: null });
                sessionStorage.setItem('CurrentAssessmentFlow', FlowType.DataAssessment)
                return false
            },
            [Assessments_IDs.AI_Readiness]: () => {
                option = idToTextObj[Assessments_IDs.AI_Readiness];
                appStateContext?.dispatch({ type: 'UPDATE_ASSESSMENT_CHAT', payload: null });
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_AI_CHAT', payload: null });
                sessionStorage.setItem('CurrentAssessmentFlow', FlowType.AIAssessment)
                return false
                }
        };

        const matchingKey = Object.keys(actions).find(key => option.toLowerCase().includes(key.toLowerCase()));
        console.log("matchingKey", matchingKey, actions[matchingKey || ''], lastMessage)

        if (matchingKey) {
            await delayResponse(300);
            return actions[matchingKey]();
        } else if (lastMessage?.flowtype == FlowType.AskForEmail) {
            sendData({ email: option })
            // saveToDB(email); TODO: Implement this 
            askMoreDeatils(option);
            return true
        } else if (assessmentFlow) {

            if (assessmentQuestions?.length == assessmentQuestionsIndex + 1) {
                const assessmentConversation = appStateContext?.state?.assessmentChat || []
                assessmentConversation.push(`USER: ${option}`);
                appStateContext?.dispatch({ type: 'UPDATE_ASSESSMENT_CHAT', payload: assessmentConversation });
                return false
            } else {
                await delayResponse(300);
                nextActionConversation(option, [
                    {
                        id: uuid(),
                        role: "assistant",
                        content: assessmentQuestions?.[assessmentQuestionsIndex + 1] || '',
                        date: new Date().toISOString(),
                        flowtype: FlowType.AssessmentQuestion,
                        messageType: MessageType.Text,
                    },
                ])
                const assessmentConversation = appStateContext?.state?.assessmentChat || []
                assessmentConversation.push(`USER: ${option}`);
                assessmentConversation.push(`ASSISTANT: ${assessmentQuestions?.[assessmentQuestionsIndex + 1]}`);
                setAssessmentCurrentQuestion(assessmentQuestions?.[assessmentQuestionsIndex + 1] || null)
                appStateContext?.dispatch({ type: 'UPDATE_ASSESSMENT_CHAT', payload: assessmentConversation });
                setAssessmentQuestionsIndex(assessmentQuestionsIndex + 1)
                setShowSuggestion(true)
                return true
            }
        }
        return false;
    }

    const makeApiRequestWithoutCosmosDB = async (question: string, conversationId?: string, flType?: FlowType) => {
        setShowSuggestion(false)

        const resp = await requestInterceptor(question);
        if (resp) {
            return;
        }

        question = idToTextObj[question as keyof typeof idToTextObj] || question;

        setIsLoading(true);
        setShowLoadingMessage(true);

        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);
        let flowtype: FlowType = flType || FlowType.ConversationStarter;
        if (assessmentFlow && assessmentQuestions?.length == assessmentQuestionsIndex + 1) {
            flowtype = `${assessmentFlow}${FlowType.FinalScore}` as FlowType;
        }

        const userMessage: ChatMessage = {
            id: uuid(),
            role: "user",
            content: question,
            date: new Date().toISOString(),
            flowtype: flowtype,
            messageType: MessageType.Text,
        };

        let conversation: Conversation | null | undefined;
        if (!conversationId) {
            conversation = {
                id: conversationId ?? uuid(),
                title: question,
                messages: [userMessage],
                date: new Date().toISOString(),
            }
        } else {
            conversation = appStateContext?.state?.currentChat
            if (!conversation) {
                console.error("Conversation not found.");
                setIsLoading(false);
                setShowLoadingMessage(false);
                abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                return;
            } else {
                conversation.messages = conversation.messages.filter((answer) => answer.messageType !== MessageType.Options);
                conversation.messages.push(userMessage);
            }
        }

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
        setMessages(conversation.messages)

        const aiConversation = appStateContext?.state?.currentAIChat || {
            id: uuid(),
            title: question,
            messages: [],
            date: new Date().toISOString(),
        };
        aiConversation.messages.push(userMessage);
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_AI_CHAT', payload: aiConversation });


        let request: ConversationRequest = {
            messages: [...aiConversation.messages.filter((answer) => (answer.role !== ERROR))]
        };

        if (assessmentFlow && assessmentQuestions?.length == assessmentQuestionsIndex + 1) {
            const assessmentConversation = appStateContext?.state?.assessmentChat || []
            const formatRequestMessage = getFormatMessage(assessmentConversation);
            request = {
                messages: [{
                    ...userMessage,
                    content: formatRequestMessage,
                }
                ]
            };
        }

        let result = {} as ChatResponse;
        try {
            const response = await conversationApi(request, abortController.signal);
            if (response?.body) {
                const reader = response.body.getReader();

                let runningText = "";
                while (true) {
                    setProcessMessages(messageStatus.Processing)
                    const { done, value } = await reader.read();
                    if (done) break;

                    var text = new TextDecoder("utf-8").decode(value);
                    const objects = text.split("\n");
                    objects.forEach((obj) => {
                        try {
                            if (obj !== "" && obj !== "{}") {
                                runningText += obj;
                                result = JSON.parse(runningText);
                                if (result.choices?.length > 0) {
                                    result.choices[0].messages.forEach((msg) => {
                                        msg.id = result.id;
                                        msg.date = new Date().toISOString();
                                    })
                                    if (result.choices[0].messages?.some(m => m.role === ASSISTANT)) {
                                        setShowLoadingMessage(false);
                                    }
                                    result.choices[0].messages.forEach((resultObj) => {
                                        processResultMessage(resultObj, userMessage, conversationId);
                                    })
                                }
                                else if (result.error) {
                                    throw Error(result.error);
                                }
                                runningText = "";
                            }
                        }
                        catch (e) {
                            if (!(e instanceof SyntaxError)) {
                                console.error(e);
                                throw e;
                            } else {
                                console.log("Incomplete message. Continuing...")
                            }
                        }
                    });
                }

                const respIntercept = responseInterceptor(assistantMessage, conversationId)

                if (!conversationId) {
                    isEmpty(toolMessage) ?
                        setMessages([...messages, userMessage, assistantMessage, ...nextRespopnseMessage]) :
                        setMessages([...messages, userMessage, toolMessage, assistantMessage, ...nextRespopnseMessage]);
                } else {
                    isEmpty(toolMessage) ?
                        setMessages([...messages, assistantMessage, ...nextRespopnseMessage]) :
                        setMessages([...messages, toolMessage, assistantMessage, ...nextRespopnseMessage]);
                }

                conversation.messages.push(toolMessage, assistantMessage, ...nextRespopnseMessage)
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });

                if (!isEmpty(assistantMessage) && !disableAIChatUpdate) {
                    aiConversation.messages.push(assistantMessage);
                    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_AI_CHAT', payload: aiConversation });
                }
                disableAIChatUpdate = false

                setMessages([...messages, toolMessage, assistantMessage]);
            }

        } catch (e) {
            if (!abortController.signal.aborted) {
                let errorMessage = "An error occurred. Please try again. If the problem persists, please contact the site administrator.";
                if (result.error?.message) {
                    errorMessage = result.error.message;
                }
                else if (typeof result.error === "string") {
                    errorMessage = result.error;
                }
                let errorChatMsg: ChatMessage = {
                    id: uuid(),
                    role: ERROR,
                    content: errorMessage,
                    date: new Date().toISOString(),
                    flowtype: FlowType.Error,
                    messageType: MessageType.Text
                }
                conversation.messages.push(errorChatMsg);
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
                setMessages([...messages, errorChatMsg]);
            } else {
                setMessages([...messages, userMessage])
            }
        } finally {
            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
            setProcessMessages(messageStatus.Done)
        }

        return abortController.abort();
    };

    const clearChat = async () => {
        setClearingChat(true)
        if (appStateContext?.state.currentChat?.id && appStateContext?.state.isCosmosDBAvailable.cosmosDB) {
            let response = await historyClear(appStateContext?.state.currentChat.id)
            if (!response.ok) {
                setErrorMsg({
                    title: "Error clearing current chat",
                    subtitle: "Please try again. If the problem persists, please contact the site administrator.",
                })
                toggleErrorDialog();
            } else {
                appStateContext?.dispatch({ type: 'DELETE_CURRENT_CHAT_MESSAGES', payload: appStateContext?.state.currentChat.id });
                appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: appStateContext?.state.currentChat });
                setActiveCitation(undefined);
                setIsCitationPanelOpen(false);
                setMessages([])
            }
        }
        setClearingChat(false)
    };



    const stopGenerating = () => {
        abortFuncs.current.forEach(a => a.abort());
        setShowLoadingMessage(false);
        setIsLoading(false);
    }

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" })
    }, [messages])

    useEffect(() => {
        if (appStateContext?.state.currentChat) {
            setMessages(appStateContext.state.currentChat.messages);
        } else {
            setMessages([]);
        }

    }, [appStateContext?.state.currentChat]);

    useLayoutEffect(() => {
        const saveToDB = async (messages: ChatMessage[], id: string) => {
            const response = await historyUpdate(messages, id)
            return response
        }

        if (appStateContext && appStateContext.state.currentChat && processMessages === messageStatus.Done) {
            if (appStateContext.state.isCosmosDBAvailable.cosmosDB) {
                if (!appStateContext?.state.currentChat?.messages) {
                    console.error("Failure fetching current chat state.")
                    return
                }
                const noContentError = appStateContext.state.currentChat.messages.find(m => m.role === ERROR)

                if (noContentError && !noContentError.content.includes(NO_CONTENT_ERROR)) {
                    saveToDB(appStateContext.state.currentChat.messages, appStateContext.state.currentChat.id)
                        .then((res) => {
                            if (!res.ok) {
                                let errorMessage = "An error occurred. Answers can't be saved at this time. If the problem persists, please contact the site administrator.";
                                let errorChatMsg: ChatMessage = {
                                    id: uuid(),
                                    role: ERROR,
                                    content: errorMessage,
                                    date: new Date().toISOString(),
                                    flowtype: FlowType.Error,
                                    messageType: MessageType.Text
                                }
                                if (!appStateContext?.state.currentChat?.messages) {
                                    let err: Error = {
                                        ...new Error,
                                        message: "Failure fetching current chat state."
                                    }
                                    throw err
                                }
                                setMessages([...appStateContext?.state.currentChat?.messages, errorChatMsg])
                            }
                            return res as Response
                        })
                        .catch((err) => {
                            console.error("Error: ", err)
                            let errRes: Response = {
                                ...new Response,
                                ok: false,
                                status: 500,
                            }
                            return errRes;
                        })
                }
            } else {
            }
            appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: appStateContext.state.currentChat });
            setMessages(appStateContext.state.currentChat.messages)
            setProcessMessages(messageStatus.NotRunning)
        }
    }, [processMessages]);

    useEffect(() => {
        if (AUTH_ENABLED !== undefined) getUserInfoList();
    }, [AUTH_ENABLED]);

    useLayoutEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" })
    }, [showLoadingMessage, processMessages]);

    const onShowCitation = (citation: Citation) => {
        setActiveCitation(citation);
        setIsCitationPanelOpen(true);
    };

    const onViewSource = (citation: Citation) => {
        if (citation.url && !citation.url.includes("blob.core")) {
            window.open(citation.url, "_blank");
        }
    };

    const parseCitationFromMessage = (message: ChatMessage) => {
        if (message?.role && message?.role === "tool") {
            try {
                const toolMessage = JSON.parse(message.content) as ToolMessageContent;
                return toolMessage.citations;
            }
            catch {
                return [];
            }
        }
        return [];
    }

    const disabledButton = () => {
        return isLoading || (messages && messages.length === 0) || clearingChat || appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading
    }

    const latestMessage = messages[messages.length - 1];

    return (
        <div className={styles.container} role="main">
            {showKillSessionModal && <KillSessionModal showModal={showKillSessionModal} />}
            <InactiveModal />
            <Stack horizontal className={styles.chatRoot}>
                <div className={styles.chatContainer}>
                    {!messages || messages.length < 1 ? (
                        <Stack className={styles.chatEmptyState}>
                            <img
                                src={ui?.logo ? ui.logo : Contoso}
                                className={styles.chatIcon}
                                aria-hidden="true"
                            />
                            <h1 className={styles.chatEmptyStateTitle}>{ui?.chat_title}</h1>
                            <h2 className={styles.chatEmptyStateSubtitle}>{ui?.chat_description}</h2>
                        </Stack>
                    ) : (
                        <div className={styles.chatMessageStream} style={{ marginBottom: isLoading ? "40px" : "0px" }} role="log">
                            {messages.map((answer, index) => (
                                <>
                                    {answer.role === "user" ? (
                                        <UserResponsesRender
                                            answer={answer}
                                            styles={styles}
                                            conversationId={appStateContext?.state.currentChat?.id ? appStateContext?.state.currentChat?.id : undefined}
                                            onClick={makeApiRequestWithoutCosmosDB}
                                            submitFormResponse={submitFormResponse}
                                        />
                                    ) : (
                                        answer.role === "assistant" ? <><div className={styles.chatMessageGpt}>
                                            <Answer
                                                answer={{
                                                    answer: answer.content,
                                                    citations: parseCitationFromMessage(messages[index - 1]),
                                                    message_id: answer.id,
                                                    feedback: answer.feedback
                                                }}
                                                onCitationClicked={c => onShowCitation(c)}
                                            />
                                        </div>
                                            {(messages.length - 1) == index && showSuggestion &&
                                                <Suggestions
                                                    answer={answer}
                                                    populateInAnswer={(val: string) => {
                                                        setQuestion(question + (question == "" ? "" : ",") + val);
                                                    }}
                                                    scroll={() => {
                                                        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" })
                                                    }}
                                                    currentAssessmentQuestion={assessmentCurrentQuestion}
                                                    styles={styles}
                                                />
                                            }
                                        </>
                                            : answer.role === ERROR ? <div className={styles.chatMessageError}>
                                                <Stack horizontal className={styles.chatMessageErrorContent}>
                                                    <ErrorCircleRegular className={styles.errorIcon} style={{ color: "rgba(182, 52, 67, 1)" }} />
                                                    <span>Error</span>
                                                </Stack>
                                                <span className={styles.chatMessageErrorContent}>{answer.content}</span>
                                            </div> : null
                                    )}
                                </>
                            ))}
                            {showLoadingMessage && (
                                <>
                                    <div className={styles.chatMessageGpt}>
                                        <Answer
                                            answer={{
                                                answer: "Generating answer...",
                                                citations: []
                                            }}
                                            onCitationClicked={() => null}
                                        />
                                    </div>
                                </>
                            )}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}

                    {
                        (latestMessage?.messageType === MessageType.Text &&
                            latestMessage?.flowtype !== FlowType.Chatend) && (
                            <Stack horizontal className={styles.chatInput}>
                                <QuestionInput
                                    clearOnSend
                                    isEmail={latestMessage?.flowtype === FlowType.AskForEmail}
                                    placeholder="Write a reply..."
                                    disabled={isLoading || showLoadingMessage}
                                    onSend={(question, id) => {
                                        makeApiRequestWithoutCosmosDB(question, id)
                                    }}
                                    question={question}
                                    setQuestion={setQuestion}
                                    conversationId={appStateContext?.state.currentChat?.id ? appStateContext?.state.currentChat?.id : undefined}
                                />
                            </Stack>
                        )
                    }

                    {/* <Stack horizontal className={styles.chatInput}>
                        {isLoading && (
                            <Stack
                                horizontal
                                className={styles.stopGeneratingContainer}
                                role="button"
                                aria-label="Stop generating"
                                tabIndex={0}
                                onClick={stopGenerating}
                                onKeyDown={e => e.key === "Enter" || e.key === " " ? stopGenerating() : null}
                            >
                                <SquareRegular className={styles.stopGeneratingIcon} aria-hidden="true" />
                                <span className={styles.stopGeneratingText} aria-hidden="true">Stop generating</span>
                            </Stack>
                        )}
                        <Stack>
                            <Dialog
                                hidden={hideErrorDialog}
                                onDismiss={handleErrorDialogClose}
                                dialogContentProps={errorDialogContentProps}
                                modalProps={modalProps}
                            >
                            </Dialog>
                        </Stack>
                      
                    </Stack> */}
                </div>
            </Stack>
        </div>
    );
};

export default NSChat;
